/**
 * user agent checks inspired by https://github.com/lancedikson/bowser
 */
import { memorize } from './memorize.js';
import { isFunction } from './typeguards.js';

declare global {
    interface Navigator {
        /** Returns a boolean indicating whether the browser is running in standalone mode. Available on Apple's iOS Safari only. */
        readonly standalone?: boolean;
    }
}

type CacheNavigator = Pick<Navigator, "userAgent" | "standalone" | "maxTouchPoints">


/**
 * All known operating systems by our detection.
 * Other OS'es will be classified as UNKNOWN.
 */
export enum Os {
    WINDOWS = 'Windows',
    LINUX = 'Linux',
    iOS = 'iOS',
    MacOS = 'MacOS',
    ANDROID = 'Android',
    WebOS = 'webOS',
    ChromeOS = 'ChromeOS',
    UNKNOWN = 'unknown',
}

/**
 * All known browsers by our detection.
 * Other browsers will be classified as UNKNOWN.
 */
export enum Browser {
    Googlebot = 'Googlebot',
    Opera = 'Opera',
    SamsungBrowser = 'SamsungBrowser',
    InternetExplorer = 'MSIE',
    Edge = 'Edge',
    Firefox = 'Firefox',
    Chrome = 'Chrome',
    Safari = 'Safari',
    UNKNOWN = 'unknown',
}

/**
 * Browser details returned by getBrowserDetails()
 * This is the combined result of former getOs() and getBrowserData().
 */
export interface BrowserDetailsInterface {
    /** Operating System */
    os: Os;
    /** Browser Name */
    browserName: Browser;
    /** Browser major version */
    browserVersion: number;
    /** is the device a tablet? */
    isTablet: boolean;
    /** is the device a phone? */
    isPhone: boolean;
}

/**
 * Describing a check/rule to grab some details about the browser and os.
 */
interface Rule {
    /** a function or array of regular expressions to select the rule for evaluation */
    select: RegExp[] | ((navigator: Navigator) => boolean),
    /** a function to get some details about the browser */
    details: (ua: string) => Partial<BrowserDetailsInterface>,
}

/** a helper function to extract a version number from a string */
const intMatch = (str: string, regexp: RegExp, num: number): number => parseInt(str.match(regexp)?.[num] ?? '');

// a shared regex pattern for a version used in many user agent strings
const commonVersionIdentifier: RegExp = /version\/(\d+(\.?_?\d+)+)/i;

/**
 * Defining all checks/rules to detect the operating system.
 * The order of the checks/rules is important as the first matching check/rule is used.
 */
const osChecks: Rule[] = [
    {
        select: [/windows /i],
        details: () => ({ os: Os.WINDOWS }),
    },
    {
        select: ({ userAgent, standalone, maxTouchPoints }) => {
            /**
             * userAgent includes any iOS device identified by ipod/iphone/ipad/crios/fxios
             * or device is an iPad without "iPad" in userAgent but "Macintosh" and the iOS-only property "standalone" is defined (true or false does not matter)
             * and has touch support (maxTouchPoints > 0) to distinguish from real Macs
             */
            return (
                (/(ipod|iphone|ipad|CriOS|FxiOS)/i).test(userAgent) ||
                (/Macintosh/i.test(userAgent) && standalone !== undefined && maxTouchPoints > 0)
            )
        },
        details: () => ({ os: Os.iOS })
    },
    {
        select: [/macintosh/i],
        details: () => ({ os: Os.MacOS }),
    },
    {
        select: [/(chromebook|crOS)/i],
        details: () => ({ os: Os.ChromeOS })
    },
    {
        select: [/android(?!.*chromebook)/i],
        details: () => ({ os: Os.ANDROID }),
    },
    {
        select: [/(web|hpw)[o0]s/i],
        details: () => ({ os: Os.WebOS }),
    },
    {
        select: [/(linux|tizen)/i],
        details: () => ({ os: Os.LINUX }),
    },
];

/**
 * Defining all checks/rules to detect the device type.
 * The order of the checks/rules is important as the first matching check/rule is used.
 */
const deviceChecks: Rule[] = [
    {
        select: ({ userAgent, standalone, maxTouchPoints }) => {
            /**
             * userAgent includes anything that identifies device as tablet
             * or device is an iPad without "iPad" in userAgent but "Macintosh" and the iOS-only property "standalone" is defined (true or false does not matter)
             * and has touch support (maxTouchPoints > 0) to distinguish from real Macs
             */
            return (
                (/Macintosh/i.test(userAgent) && standalone !== undefined && maxTouchPoints > 0) ||
                (/(tab|pad|playbook|silk|Kindle)|(android|Tab)(?!.*(mobi|chromebook|crOS))|(Macintosh.*CriOS)/i).test(userAgent)
            )
        },
        details: () => ({ isTablet: true, isPhone: false })
    },
    {
        select: [/Mobile|Android(?!.*(Chromebook|crOS))|iP(hone|od)|IEMobile|BlackBerry|^(hpw|web)OS$|Opera M(obi|ini)|(windows phone)/i],
        details: () => ({ isTablet: false, isPhone: true }),
    }
];

/**
 * Defining all checks/rules to detect the browser name and its version.
 * The order of the checks/rules is important as the first matching check/rule is used.
 */
const browserChecks: Rule[] = [
    {
        // Googlebot
        select: [/googlebot/i],
        details: (ua) => ({
            browserName: Browser.Googlebot,
            browserVersion: intMatch(ua, /googlebot\/(\d+(\.\d+))/i, 1) || intMatch(ua, commonVersionIdentifier, 1),
        }),
    },
    {
        // Opera > 13.0
        select: [/opr\/|opios|opera/i],
        details: (ua) => ({
            browserName: Browser.Opera,
            browserVersion: intMatch(ua, /(?:opr|opios)[\s/](\S+)/i, 1) || intMatch(ua, commonVersionIdentifier, 1) || intMatch(ua, /(?:opera)[\s/](\d+(\.?_?\d+)+)/i, 1),
        }),
    },
    {
        select: [/SamsungBrowser/i],
        details: (ua) => ({
            browserName: Browser.SamsungBrowser,
            browserVersion: intMatch(ua, commonVersionIdentifier, 1) || intMatch(ua, /(?:SamsungBrowser)[\s/](\d+(\.?_?\d+)+)/i, 1),
        }),
    },
    {
        // Internet Explorer
        select: [/msie|trident/i],
        details: (ua) => ({
            browserName: Browser.InternetExplorer,
            browserVersion: intMatch(ua, /(?:msie |rv:)(\d+(\.?_?\d+)+)/i, 1),
        }),
    },
    {
        select: [/edg([ea]|ios)|\sedg\//i],
        details: (ua) => ({
            browserName: Browser.Edge,
            browserVersion: intMatch(ua, /edg([ea]|ios)\/(\d+(\.?_?\d+)+)/i, 2) || intMatch(ua, /\sedg\/(\d+(\.?_?\d+)+)/i, 1),
        }),
    },
    {
        select: [/firefox|iceweasel|fxios/i],
        details: (ua) => ({
            browserName: Browser.Firefox,
            browserVersion: intMatch(ua, /(?:firefox|iceweasel|fxios)[\s/](\d+(\.?_?\d+)+)/i, 1),
        }),
    },
    {
        select: [/chrome(?!.*book)|crios|crmo/i],
        details: (ua) => ({
            browserName: Browser.Chrome,
            browserVersion: intMatch(ua, /(?:chrome|crios|crmo)\/(\d+(\.?_?\d+)+)/i, 1),
        }),
    },
    {
        select: [/safari|applewebkit/i],
        details: (ua) => ({
            browserName: Browser.Safari,
            browserVersion: intMatch(ua, commonVersionIdentifier, 1),
        }),
    },
];

/**
 * Will check the user agent to determine what OS and browser is used and if the device is a mobile or tablet device.
 *
 * @param win optional window to inspect (used in tests), detaults to current window
 * @returns details about os and browser
 */
export const getBrowserDetails = (win: Window = window): BrowserDetailsInterface => {

    // grab required values from navigator and define default values
    const { userAgent = "", maxTouchPoints = 0, standalone = undefined } = win.navigator;
    const nav: CacheNavigator = { userAgent, maxTouchPoints, standalone };

    return memorize('browserDetails', [nav.userAgent, nav.maxTouchPoints, String(nav.standalone)], () => {
        // helper function to select a rule
        const matchingRule = ({ select }: Rule): boolean => isFunction(select) ? select(win.navigator) : select.some(regex => regex.test(nav.userAgent));
        // defining the browser details to return as default
        const defaultBrowserDetails: BrowserDetailsInterface = {
            os: Os.UNKNOWN,
            browserName: Browser.UNKNOWN,
            browserVersion: 0,
            isTablet: false,
            isPhone: false,
        };
        // result of detection, partially overwritten by detection rules
        return {
            ...defaultBrowserDetails,
            ...osChecks.find(matchingRule)?.details(nav.userAgent),
            ...deviceChecks.find(matchingRule)?.details(nav.userAgent),
            ...browserChecks.find(matchingRule)?.details(nav.userAgent),
        };
    });
};
